import React, { useEffect } from 'react';
import { Instagram, Facebook, Twitter, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <footer className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      <div className="container-custom py-20">
        <div className="glass-card rounded-3xl p-12 md:p-16" style={{background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)'}}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Brand Section */}
            <div className="md:col-span-6">
              <div className="flex items-center gap-2 mb-6">
                <img 
                  src="/logos/full_band_1.svg"
                  alt="Wedify Logo" 
                  className="h-8 w-auto"
                />
                <span className="text-[20px] font-bold font-cormorant-garamond text-[#1C1C1E]">Wedify</span>
              </div>
              <p className="text-[18px] font-cormorant-garamond text-[#535353] mb-6">
                The modern way to plan your perfect day. Our AI-powered wedding planning platform makes the journey from "Yes" to "I do" seamless and stress-free.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://instagram.com/wedify.ai"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram className="w-5 h-5 text-gray-600" />
                </a>
                <a
                  href="https://facebook.com/Wedifyai"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="w-5 h-5 text-gray-600" />
                </a>
                <a
                  href="https://twitter.com/wedify_ai"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter className="w-5 h-5 text-gray-600" />
                </a>
                <a
                  href="mailto:admin@wedify.ai"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mail className="w-5 h-5 text-gray-600" />
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 md:col-span-6 gap-8">
              {/* Legal Section */}
              <div>
                <h3 className="text-[14px] font-bold font-cormorant-garamond mb-4 text-black">Legal</h3>
                <ul className="space-y-3">
                  {[
                    { label: 'Terms of Service', to: '/terms-of-service' },
                    { label: 'Privacy policy', to: '/privacy-policy' },
                    { label: 'Pricing Plan', to: '/pricing-plan' }
                  ].map((item) => (
                    <li key={item.label}>
                      <Link to={item.to} className="text-[14px] font-cormorant-garamond text-[#535353] hover:text-gray-900 transition-colors">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Links Section */}
              <div>
                <h3 className="text-[14px] font-bold font-cormorant-garamond mb-4 text-black">Links</h3>
                <ul className="space-y-3">
                  {[
                    { label: 'Home', to: '/' },
                    { label: 'Design', to: '/design' },
                    { label: 'Plan', to: '/plan' },
                    { label: 'Explore', to: '/explore' }
                  ].map((item) => (
                    <li key={item.label}>
                      <Link to={item.to} className="text-[14px] font-cormorant-garamond text-[#535353] hover:text-gray-900 transition-colors">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Newsletter Section replaced with Vendor Marketplace CTA */}
              <div className="col-span-2 md:col-span-1">
                <h3 className="text-[14px] font-bold font-cormorant-garamond mb-4 text-black">For Vendors</h3>
                <p className="text-[13px] font-cormorant-garamond text-[#535353] mb-4">
                  Are you a wedding vendor? <br />
                  <span className='italic'>Join the Wedify Marketplace to connect with engaged couples and grow your business.</span>
                </p>
                <div data-tf-live="01JV5M9VR8A118AG69E6PGMXZH"></div>
              </div>
            </div>
          </div>
          {/* Copyright Section */}
          <div className="text-center mt-12 pt-8 border-t border-white/10">
            <p className="text-[13px] font-cormorant-garamond text-[#535353] text-sm">
              © 2025 Wedify, Inc. All rights reserved. The modern way to plan your <span className='italic'>"Yes" to "I do"</span>.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;